import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { dismissAllToasts } from '@/lib/toast';
import { login, logout, fetchSession, getAuthConfig } from './api';
import type { LoginInput } from './types';

// Query keys
export const authKeys = {
  all: ['auth'] as const,
  session: () => [...authKeys.all, 'session'] as const,
  config: () => [...authKeys.all, 'config'] as const,
};

/**
 * Get current session/user
 */
export function useSession(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: authKeys.session(),
    queryFn: fetchSession,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
    enabled: options?.enabled,
  });
}

/**
 * Login mutation
 * On success: updates session cache and navigates to home
 * On error: handled by global error handler in queryClient
 */
export function useLogin() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (input: LoginInput) => login(input),
    onSuccess: (data) => {
      // Update session cache with user data
      queryClient.setQueryData(authKeys.session(), data.user);
      // Clear any stale toasts
      dismissAllToasts();
      // Navigate to home
      navigate('/');
    },
    // Errors are handled by global mutationCache.onError in queryClient.ts
  });
}

/**
 * Logout mutation
 * Clears session and navigates to login
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: logout,
    onSettled: () => {
      // Always clear all cached data and redirect, even if API call fails
      // This prevents stale user data from showing after re-login with a different account
      queryClient.clear();
      navigate('/login');
    },
  });
}

/**
 * Get auth configuration (password requirements, feature flags)
 */
export function useAuthConfig() {
  return useQuery({
    queryKey: authKeys.config(),
    queryFn: getAuthConfig,
    staleTime: 1000 * 60 * 60, // 1 hour - config rarely changes
  });
}
